import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Table,
  Input,
  Button,
  message,
  Space,
  Card,
  Row,
  Col,
  Typography,
  Select,
  Tag,
  Spin,
  DatePicker,
  Divider,
} from 'antd';
import { SearchOutlined, ClearOutlined } from '@ant-design/icons';
import api from '../../api/axios'; // Make sure this path is correct
import dayjs from 'dayjs'; // Import dayjs for date handling
import jsPDF from 'jspdf';
import autoTable  from 'jspdf-autotable';

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

// This object remains useful for mapping log types to UI elements
const logTypeOptions = {
  'ADD': { text: 'Stock Add', color: 'green' },
  'SALE': { text: 'Sale', color: 'red' },
  'TRANSFER_IN': { text: 'Transfer In', color: 'cyan' },
  'TRANSFER_OUT': { text: 'Transfer Out', color: 'purple' },
  'ADJUSTMENT': { text: 'Adjustment', color: 'orange' },
  'RETURN': { text: 'Return', color: 'blue' },
};


const SalesLog = () => {
  // --- STATES ---
  const [logs, setLogs] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [messageApi, contextHolder] = message.useMessage();

  // --- FILTER STATES ---
  const [searchText, setSearchText] = useState('');
  const [selectedWarehouse, setSelectedWarehouse] = useState(null);
  const [selectedLogType, setSelectedLogType] = useState(null);
  const [dateRange, setDateRange] = useState(null);

  // --- DATA FETCHING ---
  // We use useCallback to memoize this function so it can be used in useEffect dependencies
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch both warehouses and inventory logs in parallel for better performance
      const [warehouseRes, logsRes] = await Promise.all([
        api.get('/api/warehouses'),
        api.get('/api/inventory-logs') // The live API endpoint for logs
      ]);
      
      setWarehouses(warehouseRes.data);
      setLogs(logsRes.data);
      
      messageApi.success('Logs loaded successfully.');
    } catch (error) {
      console.error("Failed to fetch data:", error);
      messageApi.error(error.response?.data?.message || "Could not load required data.");
    } finally {
      setLoading(false);
    }
  }, [messageApi]); // messageApi is a stable dependency

  // Fetch data when the component first mounts
  useEffect(() => {
    fetchData();
  }, [fetchData]);


const exportToPDF = () => {
  if (!filteredLogs || filteredLogs.length === 0) {
    messageApi.warning('No data to export.');
    return;
  }

  const doc = new jsPDF('landscape');

  doc.setFontSize(16);
  doc.text('Inventory Audit Log Report', 14, 15);

  doc.setFontSize(10);
  doc.text(`Generated: ${dayjs().format('YYYY-MM-DD HH:mm:ss')}`, 14, 22);

  autoTable(doc, {
    head: [[
      'Type',
      'Part Name',
      'Part Number',
      'Importer ID',
      'Qty',
      'Warehouse',
      'User',
      'Reference',
      'Date',
      'Details',
    ]],
    body: filteredLogs.map(log => [
      log.type,
      log.sparepart?.partName || '',
      log.sparepart?.partNumber || '',
      log.sparepart?.importerId || '',
      log.quantity,
      log.warehouse?.name || '',
      log.user?.username || '',
      log.referenceId || '',
      dayjs(log.createdAt).format('YYYY-MM-DD HH:mm'),
      log.details || '',
    ]),
    startY: 28,
    styles: { fontSize: 8 },
    headStyles: { fillColor: [22, 119, 255] },
    alternateRowStyles: { fillColor: [245, 245, 245] },
    margin: { left: 10, right: 10 },
    pageBreak: 'auto',          // ← allows multiple pages
    showHead: 'everyPage',      // ← repeats header
  });

  // Optional: add page numbers
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.text(`Page ${i} of ${pageCount}`, doc.internal.pageSize.getWidth() - 40, doc.internal.pageSize.getHeight() - 10);
  }

  doc.save(`inventory-log-${dayjs().format('YYYY-MM-DD-HH-mm')}.pdf`);
  messageApi.success('PDF report exported successfully.');
};



  // --- CLIENT-SIDE FILTERING LOGIC ---
  const filteredLogs = useMemo(() => {
    let data = logs;

    // 1. Filter by Date Range
    if (dateRange && dateRange[0] && dateRange[1]) {
      const [startDate, endDate] = dateRange;
      data = data.filter(log => {
        const logDate = dayjs(log.createdAt); // Use 'createdAt' from the model
        return logDate.isAfter(startDate.startOf('day')) && logDate.isBefore(endDate.endOf('day'));
      });
    }

    // 2. Filter by Warehouse
    if (selectedWarehouse) {
      data = data.filter(log => log.warehouse?.id === selectedWarehouse);
    }

    // 3. Filter by Log Type
    if (selectedLogType) {
      data = data.filter(log => log.type === selectedLogType);
    }

    // 4. Filter by Search Text
    const query = searchText.toLowerCase();
    if (query) {
      data = data.filter(log => {
        const partName = log.sparepart?.partName?.toLowerCase() || '';
        const partNumber = log.sparepart?.partNumber?.toLowerCase() || '';
        const importerId = log.sparepart?.importerId?.toLowerCase() || '';
        const referenceId = log.referenceId?.toLowerCase() || '';
        const user = log.user?.username?.toLowerCase() || ''; // Assuming user object has a username
        
        return partName.includes(query) || partNumber.includes(query) || importerId.includes(query) || referenceId.includes(query) || user.includes(query);
      });
    }

    return data;
  }, [logs, dateRange, selectedWarehouse, selectedLogType, searchText]);

  // --- ACTIONS ---
  const clearFilters = () => {
    setDateRange(null);
    setSelectedWarehouse(null);
    setSelectedLogType(null);
    setSearchText('');
    messageApi.info('All filters have been cleared.');
  };

  // --- TABLE COLUMNS ---
  const columns = [
    {
      title: 'Type', dataIndex: 'type', key: 'type', fixed: 'left', width: 120,
      render: (type) => {
        const { text, color } = logTypeOptions[type] || { text: 'Unknown', color: 'default' };
        return <Tag color={color}>{text}</Tag>;
      },
    },
    {
      title: 'Part Details', key: 'partDetails', width: 250,
      render: (_, record) => (
        <div>
          <Text strong>{record.sparepart?.partName || 'N/A'}</Text>  

          <Text type="secondary">{record.sparepart?.partNumber} <br></br> {record.sparepart?.importerId}</Text>
        </div>
      ),
    },
    {
      title: 'Qty', dataIndex: 'quantity', key: 'quantity', width: 80, align: 'center',
      render: (qty) => <Text strong style={{ color: qty > 0 ? '#52c41a' : '#f5222d' }}>{qty > 0 ? `+${qty}` : qty}</Text>,
    },
    {
      title: 'Date & Time', dataIndex: 'createdAt', key: 'date', width: 200,
      render: (date) => dayjs(date).format('YYYY-MM-DD HH:mm:ss'),
      sorter: (a, b) => dayjs(b.createdAt).unix() - dayjs(a.createdAt).unix(),
      defaultSortOrder: 'descend',
    },
    { title: 'Warehouse', dataIndex: ['warehouse', 'name'], key: 'warehouse', width: 150 },
    {
      title: 'User / Ref ID', key: 'audit', width: 200,
      render: (_, record) => (
        <div>
          <Text>User: {record.user?.username || 'N/A'}</Text>  

          <Text type="secondary">Ref: {record.referenceId}</Text>
        </div>
      ),
    },
    { title: 'Details', dataIndex: 'details', key: 'details', width: 250 },
  ];

  return (
    <>
      {contextHolder}
      <Card style={{ margin: '16px', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
        <Title level={4} style={{ margin: 0 }}>Inventory Audit Log</Title>
        <Text type="secondary">Track all stock movements across your business.</Text>
        <Divider />

        {/* --- Filter Controls --- */}
        <Row gutter={[16, 16]} align="bottom">
          <Col xs={24} sm={12} lg={8}>
            <Text>Search Part or Reference</Text>
            <Input
              placeholder="Part Name, Number, ID, Ref ID, User..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} lg={5}>
            <Text>Date Range</Text>
            <RangePicker value={dateRange} onChange={setDateRange} style={{ width: '100%' }} />
          </Col>
          <Col xs={12} sm={6} lg={4}>
            <Text>Warehouse</Text>
            <Select value={selectedWarehouse} onChange={setSelectedWarehouse} style={{ width: '100%' }} placeholder="All" allowClear>
              {warehouses.map(w => <Option key={w.id} value={w.id}>{w.name}</Option>)}
            </Select>
          </Col>
          <Col xs={12} sm={6} lg={4}>
            <Text>Log Type</Text>
            <Select value={selectedLogType} onChange={setSelectedLogType} style={{ width: '100%' }} placeholder="All" allowClear>
              {Object.entries(logTypeOptions).map(([key, { text }]) => <Option key={key} value={key}>{text}</Option>)}
            </Select>
          </Col>
          <Col xs={24} sm={24} lg={3}>
            <Button icon={<ClearOutlined />} onClick={clearFilters} style={{ width: '100%' }}>
              Clear Filters
            </Button>
          </Col>
          <Col xs={24} sm={24} lg={4}>
  <Button
    type="primary"
    onClick={exportToPDF}
      disabled={loading || !filteredLogs.length}
    style={{ width: '100%' }}
  >
    Export To PDF
  </Button>
</Col>

        </Row>

        {/* --- Data Table --- */}
        <Spin spinning={loading}>
          <Table
            style={{ marginTop: '24px' }}
            columns={columns}
            dataSource={filteredLogs}
            rowKey="id"
            pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOptions: ['10', '20', '50'] }}
            scroll={{ x: 1200 }} // Set a min-width for horizontal scrolling on small screens
          />
        </Spin>
      </Card>
    </>
  );
};

export default SalesLog;
